import { localizarPontoNoMundo } from './ancoragem'
import type { CenaTabletop, ObjetoTabletop, ReferenciaPonto, VetorTabletop } from './tipos'

/**
 * Para onde puxam o mosquetão: cada corda que passa por ele ou termina nele e
 * cada perna de ancoragem presa nele. Daí sai o eixo em que ele se alinha (o
 * mosquetão gira até a carga ficar no eixo maior) e o aviso de carga lateral:
 * uma puxada fora desse eixo carrega o mosquetão de lado (carga triaxial), e a
 * resistência dele cai muito fora do eixo maior.
 *
 * Uma corda que entra e sai pelo mosquetão (um desvio) conta como UMA puxada,
 * na resultante das duas pernas: ela apoia no fundo do mosquetão. Só se as
 * pernas estão quase opostas (a corda atravessa reto) contam as duas.
 * TypeScript puro.
 */

type V = VetorTabletop

/** Puxada a mais de tanto do eixo maior conta como lateral, em graus. */
export const ANGULO_CARGA_LATERAL_GRAUS = 60

const sub = (a: V, b: V): V => [a[0] - b[0], a[1] - b[1], a[2] - b[2]]
const soma = (a: V, b: V): V => [a[0] + b[0], a[1] + b[1], a[2] + b[2]]
const comp = (a: V) => Math.hypot(a[0], a[1], a[2])
const unit = (a: V): V | null => {
  const n = comp(a)
  return n > 1e-9 ? [a[0] / n, a[1] / n, a[2] / n] : null
}
const mesmo = (a: ReferenciaPonto, idObjeto: string) => a.idObjeto === idObjeto

/** As direções (unitárias, a partir do mosquetão) em que ele é puxado. */
export function puxadasNoMosquetao(cena: CenaTabletop, objeto: ObjetoTabletop): V[] {
  const centro = localizarPontoNoMundo(cena, { idObjeto: objeto.id, idPonto: 'passagem' })
  if (!centro) return []
  const para = (ref: ReferenciaPonto | undefined): V | null => {
    if (!ref) return null
    const p = localizarPontoNoMundo(cena, ref)
    return p ? unit(sub(p, centro)) : null
  }
  const puxadas: V[] = []
  for (const corda of cena.cordas ?? []) {
    corda.percurso.forEach((ref, k) => {
      if (!mesmo(ref, objeto.id)) return
      const antes = para(corda.percurso[k - 1])
      const depois = para(corda.percurso[k + 1])
      if (antes && depois) {
        const resultante = unit(soma(antes, depois))
        // corda que atravessa reto: duas puxadas opostas; desvio: uma, na resultante
        if (resultante && comp(soma(antes, depois)) > 0.2) puxadas.push(resultante)
        else puxadas.push(antes, depois)
      } else if (antes) puxadas.push(antes)
      else if (depois) puxadas.push(depois)
    })
  }
  for (const a of cena.ancoragens ?? []) {
    if (mesmo(a.centro, objeto.id)) {
      const pernas = a.fixacoes.map((f) => para(f)).filter((u): u is V => u !== null)
      puxadas.push(...pernas)
      // a carga pendura no ponto central, no sentido contrário às pernas
      const carga = unit(pernas.reduce<V>((s, u) => sub(s, u), [0, 0, 0]))
      if (carga) puxadas.push(carga)
    }
    if (a.fixacoes.some((f) => mesmo(f, objeto.id))) {
      const u = para(a.centro)
      if (u) puxadas.push(u)
    }
  }
  return puxadas
}

export interface OrientacaoMosquetao {
  /** O eixo maior, no mundo (unitário); `null` se nada puxa o mosquetão. */
  readonly eixo: V | null
  /** A maior distância de uma puxada ao eixo, em graus (0 a 90). */
  readonly maiorDesvioGraus: number
  readonly cargaLateral: boolean
}

const aplicar = (puxadas: readonly V[], v: V): V =>
  puxadas.reduce<V>((s, u) => {
    const d = u[0] * v[0] + u[1] * v[1] + u[2] * v[2]
    return [s[0] + u[0] * d, s[1] + u[1] * d, s[2] + u[2] * d]
  }, [0, 0, 0])

/**
 * O eixo em que as puxadas se alinham: a direção principal de Σ u·uᵀ, por
 * iteração de potência. Parte de cada puxada e fica com a de maior Σ (u·v)²,
 * porque um começo que caia exatamente noutro autovetor não sai dele.
 */
function eixoPrincipal(puxadas: readonly V[]): V | null {
  let melhor: { v: V; q: number } | null = null
  for (const inicio of puxadas) {
    let v = inicio
    for (let k = 0; k < 60; k += 1) {
      const n = unit(aplicar(puxadas, v))
      if (!n) break
      v = n
    }
    const q = puxadas.reduce((s, u) => s + (u[0] * v[0] + u[1] * v[1] + u[2] * v[2]) ** 2, 0)
    if (!melhor || q > melhor.q + 1e-12) melhor = { v, q }
  }
  return melhor?.v ?? null
}

export function orientacaoDoMosquetao(cena: CenaTabletop, objeto: ObjetoTabletop): OrientacaoMosquetao {
  const puxadas = puxadasNoMosquetao(cena, objeto)
  const eixo = eixoPrincipal(puxadas)
  if (!eixo) return { eixo: null, maiorDesvioGraus: 0, cargaLateral: false }
  const maiorDesvioGraus = Math.max(
    ...puxadas.map((u) => (Math.acos(Math.min(1, Math.abs(u[0] * eixo[0] + u[1] * eixo[1] + u[2] * eixo[2]))) * 180) / Math.PI),
  )
  return { eixo, maiorDesvioGraus, cargaLateral: maiorDesvioGraus > ANGULO_CARGA_LATERAL_GRAUS }
}
